import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaPalette,
  FaSpinner,
  FaUser,
  FaUsers,
  FaPlay,
  FaVideo,
} from 'react-icons/fa';
import SEO from '../../../components/common/SEO';
import Breadcrumb from '../../../components/common/Breadcrumb';
import PageSection from '../../../components/common/PageSection';
import { useAuth } from '../../../context/AuthContext';
import { fetchMyEnrollments } from '../../../services/courseService';
import { useEnrollCourse } from '../../../hooks/useEnrollCourse';
import useStaffHeaders from '../../../hooks/useStaffHeaders';
import { fetchCourseAccess } from '../../../services/learnService';

import TeacherEnrollNote from '../../../components/common/TeacherEnrollNote';
const DynamicCourseDetail = ({ course }) => {
  const { isStudent, isTeacher, getAuthHeaders } = useAuth();
  const { enroll, enrollingId } = useEnrollCourse();
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [access, setAccess] = useState(null);
  const { getHeaders: getPlayerHeaders, hasToken } = useStaffHeaders('player');
  const slug = course.slug || course.id;

  // Enrolled students, the course's teacher and admins can open the class player.
  useEffect(() => {
    if (!hasToken || !course.slug) return;
    fetchCourseAccess(course.slug, getPlayerHeaders()).then(setAccess).catch(() => setAccess(null));
  }, [course.slug, hasToken, getPlayerHeaders, isEnrolled]);
  const isStaff = access?.role === 'staff';

  useEffect(() => {
    if (!isStudent) return;
    fetchMyEnrollments(getAuthHeaders())
      .then((enrollments) => {
        setIsEnrolled(enrollments.some((entry) => String(entry.course_id) === String(course.id)));
      })
      .catch(() => {});
  }, [course.id, isStudent, getAuthHeaders]);

  const handleEnroll = () => {
    enroll(course.id, course.title, () => setIsEnrolled(true));
  };

  const isEnrolling = enrollingId === course.id;

  return (
    <>
      <SEO
        title={`${course.title} | Arvix Creative Courses`}
        description={course.description}
        keywords={`${course.title}, graphic design course, arvix creative, ${course.level}`}
      />

      <PageSection className="pt-24 sm:pt-28 md:pt-32 pb-16 md:pb-24 min-h-[70vh]">
        <div>
          <Breadcrumb
            items={[{ label: "Courses", to: "/courses" }, { label: course.title }]}
            className="mb-8"
          />

          <div className="p-6 md:p-10 bg-white/80 backdrop-blur-sm rounded-3xl border border-red-100/50 shadow-red">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-red-50 to-red-100 text-designer-primary mb-6">
              <FaPalette className="w-8 h-8" />
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance mb-4">
              {course.title}
            </h1>

            <p className="text-gray-600 leading-relaxed mb-8">{course.description}</p>

            <div className="flex flex-wrap gap-4 mb-8">
              {course.duration && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FaClock className="text-designer-primary" />
                  <span>{course.duration}</span>
                </div>
              )}
              {course.level && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FaUser className="text-designer-primary" />
                  <span>{course.level}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FaUsers className="text-designer-primary" />
                <span>{course.enrollment_count || 0} enrolled</span>
              </div>
            </div>

            {course.teacher_name && (
              <p className="text-sm text-gray-500 mb-8">
                Instructor: <span className="font-semibold text-designer-dark">{course.teacher_name}</span>
              </p>
            )}

            {isEnrolled || isStaff ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/learn/${slug}`}
                  data-testid="watch-classes"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-red-500/30 transition-all"
                >
                  <FaPlay className="w-4 h-4" /> Watch classes
                </Link>
                {isStaff ? (
                  <Link
                    to={`/admin/studio/${slug}`}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-designer-dark font-semibold rounded-xl border border-gray-200 hover:border-designer-primary transition-all"
                  >
                    <FaVideo className="w-4 h-4" /> Manage in Studio
                  </Link>
                ) : (
                  <span className="inline-flex items-center justify-center gap-2 px-5 py-3.5 text-green-600 font-semibold">
                    <FaCheckCircle className="w-4 h-4" /> Enrolled
                  </span>
                )}
              </div>
            ) : (
              isTeacher ? (
                <TeacherEnrollNote />
              ) : (
                <button
                  type="button"
                  onClick={handleEnroll}
                  disabled={isEnrolling}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-red-500/30 hover:scale-[1.02] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                >
                  {isEnrolling ? (
                    <>
                      <FaSpinner className="animate-spin w-4 h-4" />
                      Enrolling...
                    </>
                  ) : (
                    <>
                      Enroll Now
                      <FaArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )
            )}
          </div>
        </div>
      </PageSection>
    </>
  );
};

export default DynamicCourseDetail;
